import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import "./PerformerEventsAdmin.css";
import {
  createPerformerEvent,
  deletePerformerEvent,
  getPerformerEvents,
  updatePerformerEvent,
} from "../data/songRequestsApi";

function toDateTimeLocalValue(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  const localDate = new Date(date.getTime() - offsetMs);

  return localDate.toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value) {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return null;
  }

  const date = new Date(cleanValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

function formatEventTime(
  value,
  locale = "fi-FI",
  notSetLabel = "Ei asetettu"
) {
  if (!value) {
    return notSetLabel;
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: "Europe/Helsinki",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}


function padDateTimePart(value) {
  return String(value).padStart(2, "0");
}

function parseLocalDateTime(value) {
  const match = String(value || "").match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
  );

  if (!match) {
    return null;
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

function buildDateKey(year, month, day) {
  return [
    year,
    padDateTimePart(month),
    padDateTimePart(day),
  ].join("-");
}

function clampLocalDateTime(value, min, max) {
  let nextValue = value;

  if (min && nextValue < min) {
    nextValue = min;
  }

  if (max && nextValue > max) {
    nextValue = max;
  }

  return nextValue;
}

function LocalizedDateTimeInput({
  inputRef,
  id,
  value,
  onChange,
  min,
  max,
  required = false,
  hasError = false,
  ariaDescribedBy,
  locale = "fi-FI",
  notSetLabel = "Ei asetettu",
}) {
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const parsedValue = useMemo(
    () => parseLocalDateTime(value),
    [value]
  );

  const now = new Date();
  const [viewYear, setViewYear] = useState(
    parsedValue?.year || now.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    parsedValue ? parsedValue.month - 1 : now.getMonth()
  );
  const [selectedDate, setSelectedDate] = useState(
    parsedValue
      ? buildDateKey(
          parsedValue.year,
          parsedValue.month,
          parsedValue.day
        )
      : ""
  );
  const [selectedHour, setSelectedHour] = useState(
    padDateTimePart(parsedValue?.hour ?? now.getHours())
  );
  const [selectedMinute, setSelectedMinute] = useState(
    padDateTimePart(parsedValue?.minute ?? now.getMinutes())
  );

  useEffect(() => {
    if (!parsedValue) {
      setSelectedDate("");
      return;
    }

    setViewYear(parsedValue.year);
    setViewMonth(parsedValue.month - 1);
    setSelectedDate(
      buildDateKey(
        parsedValue.year,
        parsedValue.month,
        parsedValue.day
      )
    );
    setSelectedHour(padDateTimePart(parsedValue.hour));
    setSelectedMinute(padDateTimePart(parsedValue.minute));
  }, [parsedValue]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(new Date(viewYear, viewMonth, 1, 12));

  const weekdayLabels = useMemo(() => {
    // 1.1.2024 oli maanantai, joten tästä saadaan ma-su oikeassa järjestyksessä.
    const monday = new Date(2024, 0, 1, 12);

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);

      return new Intl.DateTimeFormat(locale, {
        weekday: "short",
      })
        .format(date)
        .replace(".", "");
    });
  }, [locale]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1, 12);
    const daysFromMonday = (firstDay.getDay() + 6) % 7;
    const startDate = new Date(
      viewYear,
      viewMonth,
      1 - daysFromMonday,
      12
    );

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);

      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        monthIndex: date.getMonth(),
        day: date.getDate(),
      };
    });
  }, [viewYear, viewMonth]);

  const minDate = min ? min.slice(0, 10) : "";
  const maxDate = max ? max.slice(0, 10) : "";

  function saveValue(nextDate, nextHour, nextMinute) {
    if (!nextDate) {
      return;
    }

    const nextValue = `${nextDate}T${nextHour}:${nextMinute}`;
    onChange(clampLocalDateTime(nextValue, min, max));
  }

  function selectDay(day) {
    const dateKey = buildDateKey(day.year, day.month, day.day);

    if (
      (minDate && dateKey < minDate) ||
      (maxDate && dateKey > maxDate)
    ) {
      return;
    }

    setSelectedDate(dateKey);
    saveValue(dateKey, selectedHour, selectedMinute);
  }

  function changeMonth(amount) {
    const nextDate = new Date(
      viewYear,
      viewMonth + amount,
      1,
      12
    );

    setViewYear(nextDate.getFullYear());
    setViewMonth(nextDate.getMonth());
  }

  const displayValue = value
    ? new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : notSetLabel;

  return (
    <div ref={wrapperRef} className="relative mt-2">
      <button
        ref={inputRef}
        id={id}
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className={[
          "event-field flex w-full items-center justify-between gap-3 rounded-2xl bg-black/40 px-4 py-3 text-left text-sm font-semibold text-white outline-none transition",
          hasError
            ? "border border-red-400/80 focus:border-red-300 focus:ring-2 focus:ring-red-500/20"
            : "border border-white/10 focus:border-[#ef4444]/60",
        ].join(" ")}
        aria-invalid={hasError}
        aria-required={required}
        aria-describedby={ariaDescribedBy}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className={value ? "text-white" : "text-zinc-500"}>
          {displayValue}
        </span>

        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-4 w-4 shrink-0 text-zinc-500"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7 3v3m10-3v3M4.5 9.5h15M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-modal="false"
          className="absolute left-0 top-[calc(100%+0.5rem)] z-[200] w-[min(23rem,calc(100vw-2rem))] rounded-2xl border border-white/10 bg-[#090909] p-4 text-white shadow-2xl theme-light:border-[#5a3824]/20 theme-light:bg-[#fffaf5] theme-light:text-[#241a16]"
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-current/15 text-lg font-black transition hover:bg-white/10 theme-light:hover:bg-black/5"
              aria-label={
                locale.startsWith("en")
                  ? "Previous month"
                  : "Edellinen kuukausi"
              }
            >
              ‹
            </button>

            <strong className="text-sm font-black capitalize">
              {monthLabel}
            </strong>

            <button
              type="button"
              onClick={() => changeMonth(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-current/15 text-lg font-black transition hover:bg-white/10 theme-light:hover:bg-black/5"
              aria-label={
                locale.startsWith("en")
                  ? "Next month"
                  : "Seuraava kuukausi"
              }
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {weekdayLabels.map((weekday) => (
              <div
                key={weekday}
                className="py-1 text-center text-[11px] font-black uppercase opacity-55"
              >
                {weekday}
              </div>
            ))}

            {calendarDays.map((day, index) => {
              const dateKey = buildDateKey(
                day.year,
                day.month,
                day.day
              );
              const isCurrentMonth = day.monthIndex === viewMonth;
              const isSelected = dateKey === selectedDate;
              const isDisabled =
                (minDate && dateKey < minDate) ||
                (maxDate && dateKey > maxDate);

              return (
                <button
                  key={`${dateKey}-${index}`}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => selectDay(day)}
                  className={[
                    "flex aspect-square items-center justify-center rounded-lg text-xs font-bold transition",
                    isSelected
                      ? "bg-[#ef4444] text-white"
                      : isCurrentMonth
                        ? "hover:bg-white/10 theme-light:hover:bg-black/10"
                        : "opacity-35 hover:bg-white/10 theme-light:hover:bg-black/10",
                    isDisabled
                      ? "cursor-not-allowed opacity-20"
                      : "",
                  ].join(" ")}
                >
                  {day.day}
                </button>
              );
            })}
          </div>

          <div className="mt-4 border-t border-current/10 pt-4">
            <div className="flex items-center gap-2">
              <select
                value={selectedHour}
                onChange={(event) => {
                  const nextHour = event.target.value;
                  setSelectedHour(nextHour);
                  saveValue(
                    selectedDate,
                    nextHour,
                    selectedMinute
                  );
                }}
                className="min-w-0 flex-1 rounded-xl border border-current/15 bg-black/30 px-3 py-2 text-sm font-bold text-white outline-none theme-light:bg-white theme-light:text-black"
                aria-label="HH"
              >
                {Array.from({ length: 24 }, (_, hour) => {
                  const hourValue = padDateTimePart(hour);

                  return (
                    <option key={hourValue} value={hourValue}>
                      {hourValue}
                    </option>
                  );
                })}
              </select>

              <span className="font-black">:</span>

              <select
                value={selectedMinute}
                onChange={(event) => {
                  const nextMinute = event.target.value;
                  setSelectedMinute(nextMinute);
                  saveValue(
                    selectedDate,
                    selectedHour,
                    nextMinute
                  );
                }}
                className="min-w-0 flex-1 rounded-xl border border-current/15 bg-black/30 px-3 py-2 text-sm font-bold text-white outline-none theme-light:bg-white theme-light:text-black"
                aria-label="MM"
              >
                {Array.from({ length: 60 }, (_, minute) => {
                  const minuteValue = padDateTimePart(minute);

                  return (
                    <option key={minuteValue} value={minuteValue}>
                      {minuteValue}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getEmptyForm() {
  return {
    name: "",
    startsAt: "",
    endsAt: "",
    requestOpenAt: "",
    requestCloseAt: "",
    requestLimit: "",
    isActive: true,
    notes: "",
  };
}

function getEventFormValues(event) {
  const requestLimit =
    event?.requestLimit ?? event?.request_limit ?? null;

  return {
    name: event?.name || "",
    startsAt: toDateTimeLocalValue(
      event?.startsAt || event?.starts_at
    ),
    endsAt: toDateTimeLocalValue(
      event?.endsAt || event?.ends_at
    ),
    requestOpenAt: toDateTimeLocalValue(
      event?.requestOpenAt || event?.request_open_at
    ),
    requestCloseAt: toDateTimeLocalValue(
      event?.requestCloseAt || event?.request_close_at
    ),
    requestLimit:
      requestLimit === null || requestLimit === undefined
        ? ""
        : String(requestLimit),
    isActive: event?.isActive ?? event?.is_active ?? true,
    notes: event?.notes || "",
  };
}

function buildEventPayload(form) {
  return {
    name: form.name.trim(),
    startsAt: fromDateTimeLocalValue(form.startsAt),
    endsAt: fromDateTimeLocalValue(form.endsAt),
    requestOpenAt: fromDateTimeLocalValue(form.requestOpenAt),
    requestCloseAt: fromDateTimeLocalValue(form.requestCloseAt),
    requestLimit:
      String(form.requestLimit || "").trim() === ""
        ? null
        : Number(form.requestLimit),
    isActive: form.isActive,
    notes: form.notes.trim(),
  };
}

function getDateTimeMs(value) {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return null;
  }

  const timestamp = new Date(cleanValue).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
}

function validateEventForm(form, t) {
  const errors = {};

  if (!form.name.trim()) {
    errors.name = t("eventsAdmin.validation.nameRequired", { defaultValue: "Anna tapahtumalle nimi." });
  }

  if (!form.startsAt) {
    errors.startsAt = t("eventsAdmin.validation.startRequired", {
      defaultValue: "Valitse tapahtuman alkamispäivä ja kellonaika.",
    });
  }

  if (!form.endsAt) {
    errors.endsAt = t("eventsAdmin.validation.endRequired", {
      defaultValue: "Valitse tapahtuman päättymispäivä ja kellonaika.",
    });
  }

  if (!form.requestOpenAt) {
    errors.requestOpenAt = t("eventsAdmin.validation.requestOpenRequired", {
      defaultValue: "Valitse päivä ja kellonaika, jolloin biisitoiveet avautuvat.",
    });
  }

  if (!form.requestCloseAt) {
    errors.requestCloseAt = t("eventsAdmin.validation.requestCloseRequired", {
      defaultValue: "Valitse päivä ja kellonaika, jolloin biisitoiveet sulkeutuvat.",
    });
  }

  const startsAt = getDateTimeMs(form.startsAt);
  const endsAt = getDateTimeMs(form.endsAt);
  const requestOpenAt = getDateTimeMs(form.requestOpenAt);
  const requestCloseAt = getDateTimeMs(form.requestCloseAt);

  if (
    startsAt !== null &&
    endsAt !== null &&
    endsAt <= startsAt
  ) {
    errors.endsAt = t("eventsAdmin.validation.endAfterStart", {
      defaultValue: "Tapahtuman päättymisajan pitää olla alkamisajan jälkeen.",
    });
  }

  if (
    requestOpenAt !== null &&
    requestCloseAt !== null &&
    requestCloseAt <= requestOpenAt
  ) {
    errors.requestCloseAt = t("eventsAdmin.validation.requestCloseAfterOpen", {
      defaultValue: "Biisitoiveiden sulkeutumisajan pitää olla avautumisajan jälkeen.",
    });
  }

  if (
    requestOpenAt !== null &&
    endsAt !== null &&
    requestOpenAt >= endsAt
  ) {
    errors.requestOpenAt = t("eventsAdmin.validation.requestOpenBeforeEnd", {
      defaultValue: "Biisitoiveiden pitää avautua ennen tapahtuman päättymistä.",
    });
  }

  if (
    requestCloseAt !== null &&
    endsAt !== null &&
    requestCloseAt > endsAt
  ) {
    errors.requestCloseAt = t("eventsAdmin.validation.requestCloseByEnd", {
      defaultValue: "Biisitoiveita ei voi pitää auki tapahtuman päättymisen jälkeen.",
    });
  }

  const requestLimitValue = String(
    form.requestLimit || ""
  ).trim();

  if (requestLimitValue) {
    const requestLimit = Number(requestLimitValue);

    if (
      !Number.isInteger(requestLimit) ||
      requestLimit < 1
    ) {
      errors.requestLimit = t("eventsAdmin.validation.requestLimitMin", {
        defaultValue: "Biisitoiverajan pitää olla vähintään 1.",
      });
    }
  }

  return errors;
}

export default function PerformerEventsAdmin({
  performerSlug,
}) {
  const { t, i18n } = useTranslation("admin");
  const adminLocale =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en")
      ? "en-GB"
      : "fi-FI";

  function formatLocalizedEventTime(value) {
    return formatEventTime(
      value,
      adminLocale,
      t("eventsAdmin.common.notSet", {
        defaultValue: "Ei asetettu",
      })
    );
  }

  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(getEmptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingDeleteEvent, setPendingDeleteEvent] =
    useState(null);

  const nameRef = useRef(null);
  const startsAtRef = useRef(null);
  const endsAtRef = useRef(null);
  const requestOpenAtRef = useRef(null);
  const requestCloseAtRef = useRef(null);
  const requestLimitRef = useRef(null);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const activeA =
        a.isActive ?? a.is_active ? 0 : 1;

      const activeB =
        b.isActive ?? b.is_active ? 0 : 1;

      if (activeA !== activeB) {
        return activeA - activeB;
      }

      const timeA = new Date(
        a.startsAt ||
          a.starts_at ||
          a.requestOpenAt ||
          a.request_open_at ||
          a.createdAt ||
          a.created_at ||
          0
      ).getTime();

      const timeB = new Date(
        b.startsAt ||
          b.starts_at ||
          b.requestOpenAt ||
          b.request_open_at ||
          b.createdAt ||
          b.created_at ||
          0
      ).getTime();

      return timeA - timeB;
    });
  }, [events]);

  async function refreshEvents() {
    if (!performerSlug) {
      setEvents([]);
      return;
    }

    setIsLoading(true);

    try {
      const data = await getPerformerEvents(performerSlug);

      setEvents(
        Array.isArray(data.items) ? data.items : []
      );

      setMessage("");
    } catch (error) {
      console.error(
        "Tapahtumien haku epäonnistui:",
        error
      );

      setEvents([]);

      setMessage(
        t("eventsAdmin.messages.loadFailed", {
          defaultValue: "Tapahtumien haku epäonnistui.",
        })
      );
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refreshEvents();
  }, [performerSlug]);

  useEffect(() => {
    if (!pendingDeleteEvent) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    function handleKeyDown(event) {
      if (event.key === "Escape" && !isSaving) {
        setPendingDeleteEvent(null);
      }
    }

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [pendingDeleteEvent, isSaving]);

  function clearFieldError(key) {
    setFieldErrors((current) => {
      if (!current[key]) {
        return current;
      }

      const nextErrors = {
        ...current,
      };

      delete nextErrors[key];

      return nextErrors;
    });
  }

  function updateFormValue(key, value) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));

    clearFieldError(key);
  }

  function focusFirstError(errors) {
    const fieldRefs = {
      name: nameRef,
      startsAt: startsAtRef,
      endsAt: endsAtRef,
      requestOpenAt: requestOpenAtRef,
      requestCloseAt: requestCloseAtRef,
      requestLimit: requestLimitRef,
    };

    const firstErrorKey = [
      "name",
      "startsAt",
      "endsAt",
      "requestOpenAt",
      "requestCloseAt",
      "requestLimit",
    ].find((key) => errors[key]);

    fieldRefs[firstErrorKey]?.current?.focus();
  }

  function applyEventTimesToRequestWindow() {
    if (!form.startsAt || !form.endsAt) {
      setMessage(
        t("eventsAdmin.messages.selectEventTimesFirst", {
          defaultValue: "Valitse ensin tapahtuman alkamis- ja päättymisaika.",
        })
      );

      if (!form.startsAt) {
        startsAtRef.current?.focus();
      } else {
        endsAtRef.current?.focus();
      }

      return;
    }

    setForm((current) => ({
      ...current,
      requestOpenAt: current.startsAt,
      requestCloseAt: current.endsAt,
    }));

    setFieldErrors((current) => {
      const nextErrors = {
        ...current,
      };

      delete nextErrors.requestOpenAt;
      delete nextErrors.requestCloseAt;

      return nextErrors;
    });

    setMessage(
      t("eventsAdmin.messages.requestWindowApplied", {
        defaultValue: "Biisitoiveet ajastettiin tapahtuman ajaksi.",
      })
    );
  }

  function startEditing(event) {
    setEditingId(event.id);
    setForm(getEventFormValues(event));
    setFieldErrors({});
    setMessage(
      t("eventsAdmin.messages.editing", {
        defaultValue: "Muokataan tapahtumaa.",
      })
    );

    window.requestAnimationFrame(() => {
      nameRef.current?.focus();
    });
  }

  function cancelEditing() {
    setEditingId(null);
    setForm(getEmptyForm());
    setFieldErrors({});
    setMessage("");
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = validateEventForm(form, t);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      setMessage(
        t("eventsAdmin.messages.validationFailed", {
          defaultValue:
            "Tarkista punaisella merkityt kentät. Kaikki päivämäärät ja kellonajat ovat pakollisia.",
        })
      );

      focusFirstError(errors);

      return;
    }

    setFieldErrors({});
    setIsSaving(true);

    try {
      const payload = buildEventPayload(form);
      let successMessage = "";

      if (editingId) {
        await updatePerformerEvent(
          editingId,
          payload,
          performerSlug
        );

        successMessage = t("eventsAdmin.messages.updated", {
          defaultValue: "Tapahtuman muutokset tallennettiin.",
        });
      } else {
        await createPerformerEvent(
          payload,
          performerSlug
        );

        successMessage = t("eventsAdmin.messages.created", {
          defaultValue: "Ajastettu tapahtuma lisättiin.",
        });
      }

      setEditingId(null);
      setForm(getEmptyForm());

      await refreshEvents();

      setMessage(successMessage);
    } catch (error) {
      console.error(
        "Tapahtuman tallennus epäonnistui:",
        error
      );

      setMessage(
        t("eventsAdmin.messages.saveFailed", {
          defaultValue: "Tapahtuman tallennus epäonnistui.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  }

  function openDeleteModal(eventItem) {
    setPendingDeleteEvent(eventItem);
  }

  function closeDeleteModal() {
    if (isSaving) {
      return;
    }

    setPendingDeleteEvent(null);
  }

  async function handleDelete() {
    const eventId = pendingDeleteEvent?.id;

    if (!eventId) {
      return;
    }

    setIsSaving(true);

    try {
      await deletePerformerEvent(
        eventId,
        performerSlug
      );

      if (
        String(editingId) === String(eventId)
      ) {
        setEditingId(null);
        setForm(getEmptyForm());
        setFieldErrors({});
      }

      setPendingDeleteEvent(null);

      await refreshEvents();

      setMessage(
        t("eventsAdmin.messages.deleted", {
          defaultValue: "Tapahtuma poistettiin.",
        })
      );
    } catch (error) {
      console.error(
        "Tapahtuman poisto epäonnistui:",
        error
      );

      setMessage(
        t("eventsAdmin.messages.deleteFailed", {
          defaultValue: "Tapahtuman poisto epäonnistui.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleActive(eventItem) {
    const isCurrentlyActive =
      eventItem.isActive ??
      eventItem.is_active ??
      false;

    const nextPayload = {
      ...buildEventPayload(
        getEventFormValues(eventItem)
      ),
      isActive: !isCurrentlyActive,
    };

    setIsSaving(true);

    try {
      await updatePerformerEvent(
        eventItem.id,
        nextPayload,
        performerSlug
      );

      await refreshEvents();

      setMessage(
        isCurrentlyActive
          ? t("eventsAdmin.messages.hidden", {
              defaultValue: "Tapahtuma piilotettiin yleisöltä.",
            })
          : t("eventsAdmin.messages.visible", {
              defaultValue: "Tapahtuma näytetään yleisölle.",
            })
      );
    } catch (error) {
      console.error(
        "Tapahtuman aktiivisuuden muutos epäonnistui:",
        error
      );

      setMessage(
        t("eventsAdmin.messages.visibilityFailed", {
          defaultValue: "Tapahtuman aktiivisuuden muutos epäonnistui.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  }

  function getFieldClass(fieldName) {
    return [
      "event-field mt-2 w-full rounded-2xl bg-black/40 px-4 py-3 text-sm font-semibold text-white outline-none transition",
      fieldErrors[fieldName]
        ? "border border-red-400/80 focus:border-red-300 focus:ring-2 focus:ring-red-500/20"
        : "border border-white/10 focus:border-[#ef4444]/60",
    ].join(" ");
  }

  return (
    <>
        <section className="performer-events-admin mt-8 rounded-[2rem] border border-white/10 bg-black/35 p-5 shadow-2xl shadow-black/25 sm:p-7">
               <div className="events-admin-header mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.28em] text-[#fca5a5]">
              {t("eventsAdmin.header.eyebrow", { defaultValue: "Ajastetut keikat" })}
            </p>

            <h2 className="mt-2 text-2xl font-black text-white">
              {t("eventsAdmin.header.title", { defaultValue: "Tapahtumat" })}
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-300">
              <Trans
                t={t}
                i18nKey="eventsAdmin.header.description"
                defaultValue="Luo ajastettu keikka ja määritä, milloin tapahtuma sekä sen biisitoiveet ovat avoinna. Tarkemmat käyttöohjeet löydät sivun <help>OHJE</help>-painikkeesta."
                components={{
                  help: <strong className="font-black text-white" />,
                }}
              />
            </p>
          </div>

          <button
            type="button"
            onClick={refreshEvents}
            disabled={isLoading || isSaving}
            className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-zinc-200 transition hover:border-[#ef4444]/40 hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading
              ? t("eventsAdmin.header.refreshing", { defaultValue: "Päivitetään..." })
              : t("eventsAdmin.header.refresh", { defaultValue: "Päivitä" })}
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="events-form mb-7 grid gap-4 rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-4 sm:grid-cols-2"
        >
          <div className="sm:col-span-2">
            <label
              htmlFor="event-name"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.nameLabel", { defaultValue: "Tapahtuman nimi *" })}
            </label>

            <input
              ref={nameRef}
              id="event-name"
              value={form.name}
              onChange={(event) =>
                updateFormValue(
                  "name",
                  event.target.value
                )
              }
              className={getFieldClass("name")}
              placeholder={t("eventsAdmin.form.namePlaceholder", {
                defaultValue: "Esim. TikTok-live 12.7. tai Pubikeikka Kerava",
              })}
              required
              aria-invalid={Boolean(fieldErrors.name)}
              aria-describedby={
                fieldErrors.name
                  ? "event-name-error"
                  : undefined
              }
            />

            {fieldErrors.name && (
              <p
                id="event-name-error"
                className="mt-2 text-sm font-bold text-red-300"
              >
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="event-start"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.startLabel", { defaultValue: "Tapahtuma alkaa *" })}
            </label>

            <LocalizedDateTimeInput
              inputRef={startsAtRef}
              id="event-start"
              value={form.startsAt}
              onChange={(nextValue) =>
                updateFormValue(
                  "startsAt",
                  nextValue
                )
              }
              required
              hasError={Boolean(
                fieldErrors.startsAt
              )}
              ariaDescribedBy={
                fieldErrors.startsAt
                  ? "event-start-error"
                  : undefined
              }
              locale={adminLocale}
              notSetLabel={t("eventsAdmin.common.notSet", {
                defaultValue: "Ei asetettu",
              })}
            />

            {fieldErrors.startsAt && (
              <p
                id="event-start-error"
                className="mt-2 text-sm font-bold text-red-300"
              >
                {fieldErrors.startsAt}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="event-end"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.endLabel", { defaultValue: "Tapahtuma päättyy *" })}
            </label>

            <LocalizedDateTimeInput
              inputRef={endsAtRef}
              id="event-end"
              min={form.startsAt || undefined}
              value={form.endsAt}
              onChange={(nextValue) =>
                updateFormValue(
                  "endsAt",
                  nextValue
                )
              }
              required
              hasError={Boolean(
                fieldErrors.endsAt
              )}
              ariaDescribedBy={
                fieldErrors.endsAt
                  ? "event-end-error"
                  : undefined
              }
              locale={adminLocale}
              notSetLabel={t("eventsAdmin.common.notSet", {
                defaultValue: "Ei asetettu",
              })}
            />

            {fieldErrors.endsAt && (
              <p
                id="event-end-error"
                className="mt-2 text-sm font-bold text-red-300"
              >
                {fieldErrors.endsAt}
              </p>
            )}
          </div>

          <div className="events-quick-schedule flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/25 p-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-black text-white">
                {t("eventsAdmin.form.quickScheduleTitle", { defaultValue: "Nopea ajastus" })}
              </p>

              <p className="mt-1 text-xs leading-5 text-zinc-400">
                {t("eventsAdmin.form.quickScheduleText", {
                  defaultValue:
                    "Voit asettaa biisitoiveet avautumaan keikan alkaessa ja sulkeutumaan keikan päättyessä.",
                })}
              </p>
            </div>

            <button
              type="button"
              onClick={applyEventTimesToRequestWindow}
              className="shrink-0 rounded-full border border-[#ef4444]/30 bg-[#ef4444]/10 px-4 py-2 text-xs font-black text-red-100 transition hover:bg-[#ef4444]/20"
            >
              {t("eventsAdmin.form.useEventTimes", { defaultValue: "Käytä tapahtuman aikoja" })}
            </button>
          </div>

          <div>
            <label
              htmlFor="request-open"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.requestOpenLabel", { defaultValue: "Biisitoiveet avautuvat *" })}
            </label>

            <LocalizedDateTimeInput
              inputRef={requestOpenAtRef}
              id="request-open"
              max={form.endsAt || undefined}
              value={form.requestOpenAt}
              onChange={(nextValue) =>
                updateFormValue(
                  "requestOpenAt",
                  nextValue
                )
              }
              required
              hasError={Boolean(
                fieldErrors.requestOpenAt
              )}
              ariaDescribedBy={
                fieldErrors.requestOpenAt
                  ? "request-open-error"
                  : "request-open-help"
              }
              locale={adminLocale}
              notSetLabel={t("eventsAdmin.common.notSet", {
                defaultValue: "Ei asetettu",
              })}
            />

            <p
              id="request-open-help"
              className="mt-2 text-xs leading-5 text-zinc-500"
            >
              {t("eventsAdmin.form.requestOpenHelp", {
                defaultValue: "Toiveet voivat avautua jo ennen varsinaisen keikan alkamista.",
              })}
            </p>

            {fieldErrors.requestOpenAt && (
              <p
                id="request-open-error"
                className="mt-2 text-sm font-bold text-red-300"
              >
                {fieldErrors.requestOpenAt}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="request-close"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.requestCloseLabel", { defaultValue: "Biisitoiveet sulkeutuvat *" })}
            </label>

            <LocalizedDateTimeInput
              inputRef={requestCloseAtRef}
              id="request-close"
              min={form.requestOpenAt || undefined}
              max={form.endsAt || undefined}
              value={form.requestCloseAt}
              onChange={(nextValue) =>
                updateFormValue(
                  "requestCloseAt",
                  nextValue
                )
              }
              required
              hasError={Boolean(
                fieldErrors.requestCloseAt
              )}
              ariaDescribedBy={
                fieldErrors.requestCloseAt
                  ? "request-close-error"
                  : "request-close-help"
              }
              locale={adminLocale}
              notSetLabel={t("eventsAdmin.common.notSet", {
                defaultValue: "Ei asetettu",
              })}
            />

            <p
              id="request-close-help"
              className="mt-2 text-xs leading-5 text-zinc-500"
            >
              {t("eventsAdmin.form.requestCloseHelp", {
                defaultValue: "Toiveiden pitää sulkeutua viimeistään tapahtuman päättyessä.",
              })}
            </p>

            {fieldErrors.requestCloseAt && (
              <p
                id="request-close-error"
                className="mt-2 text-sm font-bold text-red-300"
              >
                {fieldErrors.requestCloseAt}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="request-limit"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.requestLimitLabel", { defaultValue: "Biisitoiveiden enimmäismäärä" })}
            </label>

            <input
              ref={requestLimitRef}
              id="request-limit"
              type="number"
              min="1"
              step="1"
              value={form.requestLimit}
              onChange={(event) =>
                updateFormValue(
                  "requestLimit",
                  event.target.value
                )
              }
              className={getFieldClass(
                "requestLimit"
              )}
              placeholder={t("eventsAdmin.form.requestLimitPlaceholder", {
                defaultValue: "Tyhjä = ei erillistä rajaa",
              })}
              aria-invalid={Boolean(
                fieldErrors.requestLimit
              )}
              aria-describedby={
                fieldErrors.requestLimit
                  ? "request-limit-error"
                  : "request-limit-help"
              }
            />

            <p
              id="request-limit-help"
              className="mt-2 text-xs leading-5 text-zinc-500"
            >
              {t("eventsAdmin.form.requestLimitHelp", {
                defaultValue: "Jätä tyhjäksi, kun tapahtumalla ei ole omaa erillistä toiverajaa.",
              })}
            </p>

            {fieldErrors.requestLimit && (
              <p
                id="request-limit-error"
                className="mt-2 text-sm font-bold text-red-300"
              >
                {fieldErrors.requestLimit}
              </p>
            )}
          </div>

          <label className="events-visibility flex items-start gap-3 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-zinc-200">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) =>
                updateFormValue(
                  "isActive",
                  event.target.checked
                )
              }
              className="mt-1 h-4 w-4 shrink-0 accent-[#ef4444]"
            />

            <span>
              <strong className="block font-black text-white">
                {t("eventsAdmin.form.visibilityTitle", { defaultValue: "Näytä tapahtuma yleisölle" })}
              </strong>

              <span className="mt-1 block text-xs font-medium leading-5 text-zinc-400">
                {t("eventsAdmin.form.visibilityHelp", {
                  defaultValue:
                    "Poista valinta, kun haluat pitää tapahtuman tallessa mutta piilottaa sen julkiselta sivulta.",
                })}
              </span>
            </span>
          </label>

          <div className="sm:col-span-2">
            <label
              htmlFor="event-notes"
              className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400"
            >
              {t("eventsAdmin.form.notesLabel", { defaultValue: "Muistiinpanot" })}
            </label>

            <textarea
              id="event-notes"
              value={form.notes}
              onChange={(event) =>
                updateFormValue(
                  "notes",
                  event.target.value
                )
              }
              className="event-field mt-2 min-h-[5rem] w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm font-semibold text-white outline-none transition focus:border-[#ef4444]/60"
              placeholder={t("eventsAdmin.form.notesPlaceholder", {
                defaultValue: "Sisäinen huomio tapahtumasta.",
              })}
            />
          </div>

          <div className="flex flex-wrap gap-3 sm:col-span-2">
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-full bg-[#ef4444] px-5 py-3 text-sm font-black text-white transition hover:bg-[#dc2626] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving
                ? t("eventsAdmin.form.saving", { defaultValue: "Tallennetaan..." })
                : editingId
                  ? t("eventsAdmin.form.saveChanges", { defaultValue: "Tallenna muutokset" })
                  : t("eventsAdmin.form.addScheduledEvent", {
                      defaultValue: "Lisää ajastettu tapahtuma",
                    })}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
                className="rounded-full border border-white/10 px-5 py-3 text-sm font-black text-zinc-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("eventsAdmin.form.cancelEdit", { defaultValue: "Peruuta muokkaus" })}
              </button>
            )}
          </div>
        </form>

        {message && (
          <div
            className="events-empty-state rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm font-semibold text-zinc-300"
            role="status"
            aria-live="polite"
          >
            {message}
          </div>
        )}

        {isLoading ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm font-semibold text-zinc-300">
            {t("eventsAdmin.list.loading", { defaultValue: "Ladataan tapahtumia..." })}
          </div>
        ) : sortedEvents.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm font-semibold text-zinc-200 dark:text-zinc-600">
            {t("eventsAdmin.list.empty", {
              defaultValue:
                "Ei tapahtumia vielä. Lisää ensimmäinen ajastettu tapahtuma yllä olevalla lomakkeella.",
            })}
          </div>
        ) : (
          <div className="grid gap-4">
            {sortedEvents.map((eventItem) => {
              const activeRequestCount = Number(
                eventItem.activeRequestCount ??
                  eventItem.active_request_count ??
                  0
              );

              const totalRequestCount = Number(
                eventItem.totalRequestCount ??
                  eventItem.total_request_count ??
                  0
              );

              const requestLimit =
                eventItem.requestLimit ??
                eventItem.request_limit ??
                null;

              const isFull =
                eventItem.isFull ??
                eventItem.is_full ??
                false;

              const isActive =
                eventItem.isActive ??
                eventItem.is_active ??
                false;

              return (
                <article
                  key={eventItem.id}
                  className="events-card rounded-[1.5rem] border border-white/10 bg-white/[0.035] p-4"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-black text-white">
                          {eventItem.name}
                        </h3>

                        <span
                          className={
                            isActive
                              ? "rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-black text-emerald-200"
                              : "rounded-full bg-zinc-500/15 px-3 py-1 text-xs font-black text-zinc-300"
                          }
                        >
                          {isActive
                            ? t("eventsAdmin.list.visible", { defaultValue: "Näkyy yleisölle" })
                            : t("eventsAdmin.list.hidden", { defaultValue: "Piilotettu" })}
                        </span>

                        {isFull && (
                          <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-black text-amber-200">
                            {t("eventsAdmin.list.limitFull", { defaultValue: "Toiveraja täynnä" })}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 grid gap-2 text-sm text-zinc-300 sm:grid-cols-2">
                        <p>
                          {t("eventsAdmin.list.starts", { defaultValue: "Alkaa:" })}{" "}
                          {formatLocalizedEventTime(
                            eventItem.startsAt ||
                              eventItem.starts_at
                          )}
                        </p>

                        <p>
                          {t("eventsAdmin.list.ends", { defaultValue: "Päättyy:" })}{" "}
                          {formatLocalizedEventTime(
                            eventItem.endsAt ||
                              eventItem.ends_at
                          )}
                        </p>

                        <p>
                          {t("eventsAdmin.list.requestsOpen", { defaultValue: "Toiveet avautuvat:" })}{" "}
                          {formatLocalizedEventTime(
                            eventItem.requestOpenAt ||
                              eventItem.request_open_at
                          )}
                        </p>

                        <p>
                          {t("eventsAdmin.list.requestsClose", { defaultValue: "Toiveet sulkeutuvat:" })}{" "}
                          {formatLocalizedEventTime(
                            eventItem.requestCloseAt ||
                              eventItem.request_close_at
                          )}
                        </p>
                      </div>

                      <p className="mt-3 text-sm font-bold text-zinc-200">
                        {t("eventsAdmin.list.activeRequests", {
                          defaultValue: "Aktiivisia biisitoiveita",
                        })}{" "}
                        {activeRequestCount}
                        {requestLimit !== null &&
                        requestLimit !== undefined
                          ? ` / ${requestLimit}`
                          : ""}{" "}
                        · {t("eventsAdmin.list.total", { defaultValue: "yhteensä" })}{" "}
                        {totalRequestCount}
                      </p>

                      {eventItem.notes && (
                        <p className="events-card-notes mt-3 whitespace-pre-wrap rounded-2xl bg-black/25 p-3 text-sm text-zinc-300">
                          {eventItem.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          toggleActive(eventItem)
                        }
                        disabled={isSaving}
                        className="rounded-full border border-white/10 px-4 py-2 text-xs font-black text-zinc-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isActive
                          ? t("eventsAdmin.list.hideFromAudience", { defaultValue: "Piilota yleisöltä" })
                          : t("eventsAdmin.list.showToAudience", { defaultValue: "Näytä yleisölle" })}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          startEditing(eventItem)
                        }
                        disabled={isSaving}
                        className="rounded-full border border-white/10 px-4 py-2 text-xs font-black text-zinc-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {t("eventsAdmin.list.edit", { defaultValue: "Muokkaa" })}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openDeleteModal(eventItem)
                        }
                        disabled={isSaving}
                        className="rounded-full border border-red-400/30 px-4 py-2 text-xs font-black text-red-200 transition hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {t("eventsAdmin.list.delete", { defaultValue: "Poista" })}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {pendingDeleteEvent &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="events-delete-backdrop fixed inset-0 z-[100000] grid place-items-center overflow-y-auto bg-black/80 px-4 py-8 backdrop-blur-sm"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target === event.currentTarget
              ) {
                closeDeleteModal();
              }
            }}
          >
            <div
              className="events-delete-modal relative z-[100001] w-full max-w-lg overflow-hidden rounded-[2rem] border border-red-400/25 bg-[#080908] text-white shadow-2xl shadow-black/70"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-event-title"
              aria-describedby="delete-event-description"
            >
              <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-red-300">
                    {t("eventsAdmin.delete.confirmEyebrow", { defaultValue: "Vahvista poisto" })}
                  </p>

                  <h3
                    id="delete-event-title"
                    className="mt-2 text-2xl font-black tracking-[-0.04em] sm:text-3xl"
                  >
                    {t("eventsAdmin.delete.title", { defaultValue: "Poistetaanko tapahtuma?" })}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={isSaving}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-xl text-zinc-300 transition hover:border-red-400/40 hover:bg-red-500/10 hover:text-white disabled:opacity-50"
                  aria-label={t("eventsAdmin.delete.closeLabel", {
                    defaultValue: "Sulje vahvistus",
                  })}
                >
                  ×
                </button>
              </div>

              <div className="p-6">
                <div className="events-delete-warning rounded-3xl border border-red-500/20 bg-red-500/10 p-5">
                  <p className="text-lg font-black text-white">
                    {pendingDeleteEvent.name}
                  </p>

                  <p className="mt-2 text-sm text-red-100/80">
                    {formatLocalizedEventTime(
                      pendingDeleteEvent.startsAt ||
                        pendingDeleteEvent.starts_at
                    )}{" "}
                    –{" "}
                    {formatLocalizedEventTime(
                      pendingDeleteEvent.endsAt ||
                        pendingDeleteEvent.ends_at
                    )}
                  </p>

                  <p
                    id="delete-event-description"
                    className="mt-4 text-sm leading-7 text-red-100/85"
                  >
                    {t("eventsAdmin.delete.description", {
                      defaultValue:
                        "Tapahtuma poistetaan pysyvästi. Siihen liitetyt biisitoiveet säilyvät yleisessä historiassa, mutta niiden tapahtumakytkentä poistuu. Tätä toimintoa ei voi perua.",
                    })}
                  </p>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeDeleteModal}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center rounded-full border border-white/10 px-6 py-3 text-sm font-black text-zinc-300 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("eventsAdmin.delete.cancel", { defaultValue: "Peruuta" })}
                  </button>

                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center rounded-full bg-red-600 px-6 py-3 text-sm font-black text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSaving
                      ? t("eventsAdmin.delete.deleting", { defaultValue: "Poistetaan..." })
                      : t("eventsAdmin.delete.deleteButton", { defaultValue: "Poista tapahtuma" })}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}